import React, {useEffect, useRef, useState} from "react";
import {ArrowDownIcon, ArrowUpIcon, GripVerticalIcon, Trash2Icon, UploadIcon} from "lucide-react";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {MAX_UPLOAD_BYTES, MAX_UPLOAD_MB} from "../../../../../lib/uploads";
import type {EventGalleryImage} from "../../../../../types/event";

const CAPTION_MAX = 255;

function moveItem<T>(arr: T[], from: number, to: number): T[] {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

/**
 * Manages an event's gallery: upload, caption, reorder and delete. Every endpoint answers with the
 * whole gallery in its new order, which replaces local state. The backend owns the image cap and
 * answers 409 once it is reached; its message is shown as is.
 */
export default function GalleryEditor({businessId, eventId}: { businessId: string; eventId: string }) {
    const base = `/api/v2/business/${businessId}/events/${eventId}/gallery`;

    const [images, setImages] = useState<EventGalleryImage[]>([]);
    const [captions, setCaptions] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [newCaption, setNewCaption] = useState("");

    const fileRef = useRef<HTMLInputElement>(null);
    const dragFrom = useRef<number | null>(null);

    const apply = (data: unknown) => {
        const list = (Array.isArray(data) ? data : []) as EventGalleryImage[];
        setImages(list);
        setCaptions(Object.fromEntries(list.map((i) => [i.id, i.caption ?? ""])));
    };

    useEffect(() => {
        setLoading(true);
        api.get(base)
            .then(({data}) => apply(data))
            .catch((e) => notify.error(getErrMsg(e, "Failed to load the gallery")))
            .finally(() => setLoading(false));
    }, [base]);

    const run = async (action: () => Promise<{ data: unknown }>, success: string, failure: string) => {
        setBusy(true);
        try {
            const {data} = await action();
            apply(data);
            notify.success(success);
            return true;
        } catch (e) {
            notify.error(getErrMsg(e, failure));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const upload = async (file: File) => {
        if (file.size > MAX_UPLOAD_BYTES) {
            notify.error(`Image too large (max ${MAX_UPLOAD_MB}MB)`);
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        if (newCaption.trim()) fd.append("caption", newCaption.trim());
        const ok = await run(
            () => api.post(base, fd, {headers: {"Content-Type": "multipart/form-data"}}),
            "Image added to the gallery",
            "Failed to upload image",
        );
        if (ok) setNewCaption("");
    };

    const saveCaption = (img: EventGalleryImage) => {
        const next = (captions[img.id] ?? "").trim();
        if (next === (img.caption ?? "")) return;
        void run(() => api.patch(`${base}/${img.id}`, {caption: next || null}), "Caption saved", "Failed to save caption");
    };

    const reorder = (from: number, to: number) => {
        if (to < 0 || to >= images.length || from === to) return;
        const previous = images;
        const moved = moveItem(images, from, to);
        setImages(moved);
        void run(() => api.put(`${base}/order`, {imageIds: moved.map((i) => i.id)}), "Order saved", "Failed to save order")
            .then((ok) => {
                if (!ok) setImages(previous);
            });
    };

    const remove = (img: EventGalleryImage) => {
        if (!confirm("Delete this image from the gallery?")) return;
        void run(() => api.delete(`${base}/${img.id}`), "Image deleted", "Failed to delete image");
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-900">Gallery</h3>
                    <span
                        className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {images.length} {images.length === 1 ? "image" : "images"}
                    </span>
                </div>
                <p className="text-sm text-gray-600">
                    Optional extra photos shown on the event page, in the order below. Drag a row or use the
                    arrows to reorder.
                </p>
            </div>

            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.currentTarget.value = "";
                        if (f) void upload(f);
                    }}
                />
                <label htmlFor="gallery-new-caption" className="block text-sm font-medium text-gray-900">
                    Caption for the next image (optional)
                </label>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                    <input
                        id="gallery-new-caption"
                        value={newCaption}
                        maxLength={CAPTION_MAX}
                        onChange={(e) => setNewCaption(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                        placeholder="e.g. Main stage on opening night"
                    />
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        disabled={busy}
                        onClick={() => fileRef.current?.click()}
                        leftIcon={<UploadIcon className="size-4" aria-hidden="true"/>}
                    >
                        {busy ? "Working…" : "Upload image"}
                    </Button>
                </div>
                <p className="mt-2 text-xs text-gray-500">PNG, JPEG or WebP, up to {MAX_UPLOAD_MB}MB.</p>
            </div>

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading gallery…</div>
            ) : images.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">No gallery images yet.</div>
            ) : (
                <ul className="mt-6 divide-y divide-gray-100 rounded-xl border border-gray-200">
                    {images.map((img, idx) => (
                        <li
                            key={img.id}
                            draggable={!busy}
                            onDragStart={() => (dragFrom.current = idx)}
                            onDragOver={(e: React.DragEvent) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={() => {
                                const from = dragFrom.current;
                                dragFrom.current = null;
                                if (from != null) reorder(from, idx);
                            }}
                            className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center"
                        >
                            <div className="flex items-center gap-3">
                                <GripVerticalIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true"/>
                                <img
                                    src={img.imageUrl}
                                    alt={img.caption || `Gallery image ${idx + 1}`}
                                    loading="lazy"
                                    className="size-20 shrink-0 rounded-lg object-cover ring-1 ring-black/5"
                                />
                            </div>

                            <div className="min-w-0 flex-1">
                                <label htmlFor={`caption-${img.id}`} className="sr-only">
                                    Caption for image {idx + 1}
                                </label>
                                <input
                                    id={`caption-${img.id}`}
                                    value={captions[img.id] ?? ""}
                                    maxLength={CAPTION_MAX}
                                    disabled={busy}
                                    onChange={(e) => setCaptions((c) => ({...c, [img.id]: e.target.value}))}
                                    onBlur={() => saveCaption(img)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") e.currentTarget.blur();
                                    }}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                    placeholder="Add a caption (optional)"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => reorder(idx, idx - 1)}
                                    disabled={busy || idx === 0}
                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                                    aria-label={`Move image ${idx + 1} up`}
                                >
                                    <ArrowUpIcon className="size-4"/>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => reorder(idx, idx + 1)}
                                    disabled={busy || idx === images.length - 1}
                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                                    aria-label={`Move image ${idx + 1} down`}
                                >
                                    <ArrowDownIcon className="size-4"/>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(img)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-40"
                                >
                                    <Trash2Icon className="size-4"/>
                                    <span className="hidden sm:inline">Delete</span>
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
